import { create } from "zustand";
import { normalizeErrorDetails, BackendErrorDetails } from "../util/apiError";

export type AlertType = "success" | "error" | "warning" | "info";

export interface Alert {
  id: string;
  type: AlertType;
  title?: string;
  message: string;
  details: string[];
  isVisible: boolean;
  createdAt: number;
  duration: number;
  dedupeKey: string;
}

export interface AlertOptions {
  title?: string;
  details?: BackendErrorDetails;
  duration?: number;
  autoDismiss?: boolean;
  dedupeKey?: string;
}

export interface ShowAlertInput extends AlertOptions {
  message: string;
  type?: AlertType;
}

interface AlertState {
  alerts: Alert[];
  show: (input: ShowAlertInput) => string;
  showAlert: (
    message: string,
    type?: AlertType,
    details?: BackendErrorDetails,
    options?: Omit<AlertOptions, "details">
  ) => string;
  hideAlert: (id: string) => void;
  removeAlert: (id: string) => void;
  clearAlerts: () => void;
  success: (message: string, details?: BackendErrorDetails, options?: Omit<AlertOptions, "details">) => string;
  error: (message: string, details?: BackendErrorDetails, options?: Omit<AlertOptions, "details">) => string;
  warning: (message: string, details?: BackendErrorDetails, options?: Omit<AlertOptions, "details">) => string;
  info: (message: string, details?: BackendErrorDetails, options?: Omit<AlertOptions, "details">) => string;
}

const MAX_ALERTS = 4;
const EXIT_ANIMATION_MS = 220;
const DEDUPE_WINDOW_MS = 1200;

let nextAlertId = 0;

const timeouts: Record<
  string,
  {
    dismiss?: ReturnType<typeof setTimeout>;
    remove?: ReturnType<typeof setTimeout>;
  }
> = {};

const getDefaultDuration = (type: AlertType, details: string[]): number => {
  if (type === "error") return details.length > 0 ? 12000 : 9000;
  if (type === "warning") return 8000;
  if (type === "success") return 4500;
  return 6000;
};

const createAlertId = () => `${Date.now()}-${++nextAlertId}`;

const createDedupeKey = (
  type: AlertType,
  title: string | undefined,
  message: string,
  details: string[],
  dedupeKey?: string
) => dedupeKey || [type, title || "", message, ...details].join("|");

const hasSameVisibleContent = (alert: Alert, type: AlertType, message: string, details: string[]) =>
  alert.type === type &&
  alert.message === message &&
  alert.details.length === details.length &&
  alert.details.every((detail, index) => detail === details[index]);

const clearAlertTimers = (id: string) => {
  if (!timeouts[id]) return;

  if (timeouts[id].dismiss) clearTimeout(timeouts[id].dismiss);
  if (timeouts[id].remove) clearTimeout(timeouts[id].remove);
  delete timeouts[id];
};

const useAlertStore = create<AlertState>((set, get) => ({
  alerts: [],

  show: (input) => {
    const type = input.type || "info";
    const message = input.message?.trim();

    if (!message) return "";

    const details = normalizeErrorDetails(input.details);
    const dedupeKey = createDedupeKey(type, input.title, message, details, input.dedupeKey);
    const now = Date.now();

    const existingAlert = get().alerts.find(
      (alert) =>
        now - alert.createdAt < DEDUPE_WINDOW_MS &&
        (alert.dedupeKey === dedupeKey || hasSameVisibleContent(alert, type, message, details))
    );

    if (existingAlert) return existingAlert.id;

    const id = createAlertId();
    const duration = input.duration ?? getDefaultDuration(type, details);
    const newAlert: Alert = {
      id,
      type,
      title: input.title,
      message,
      details,
      isVisible: true,
      createdAt: now,
      duration,
      dedupeKey,
    };

    const currentAlerts = get().alerts;
    const nextAlerts = [newAlert, ...currentAlerts].slice(0, MAX_ALERTS);
    const removedAlerts = currentAlerts.filter(
      (alert) => !nextAlerts.some((nextAlert) => nextAlert.id === alert.id)
    );

    removedAlerts.forEach((alert) => clearAlertTimers(alert.id));
    set({ alerts: nextAlerts });

    if (input.autoDismiss !== false && duration > 0) {
      timeouts[id] = {
        dismiss: setTimeout(() => {
          get().hideAlert(id);
        }, duration),
      };
    }

    return id;
  },

  showAlert: (message, type = "info", details = [], options = {}) =>
    get().show({ ...options, message, type, details }),

  hideAlert: (id) => {
    clearAlertTimers(id);

    set((state) => ({
      alerts: state.alerts.map((alert) =>
        alert.id === id ? { ...alert, isVisible: false } : alert
      ),
    }));

    timeouts[id] = {
      remove: setTimeout(() => {
        get().removeAlert(id);
      }, EXIT_ANIMATION_MS),
    };
  },

  removeAlert: (id) => {
    clearAlertTimers(id);
    set((state) => ({ alerts: state.alerts.filter((alert) => alert.id !== id) }));
  },

  clearAlerts: () => {
    Object.keys(timeouts).forEach(clearAlertTimers);
    set({ alerts: [] });
  },

  success: (message, details = [], options = {}) =>
    get().show({ ...options, message, type: "success", details }),

  error: (message, details = [], options = {}) =>
    get().show({ ...options, message, type: "error", details }),

  warning: (message, details = [], options = {}) =>
    get().show({ ...options, message, type: "warning", details }),

  info: (message, details = [], options = {}) =>
    get().show({ ...options, message, type: "info", details }),
}));

export default useAlertStore;
